import { useState } from 'react';
import Link from '@/lib/siteLink';
import NetworkFooter from '@/components/NetworkFooter';
import { Loader2 } from 'lucide-react';
import { useGamesLanguage } from './useGamesLanguage';
import { t } from './i18n';
import { useBotTrap } from '@/lib/botTrap';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GamesFooter() {
  const { lang } = useGamesLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const { trapField, trapMeta } = useBotTrap();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (status === 'loading' || status === 'success') return;
    setStatus('loading');
    try {
      const res = await fetch(`${API}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          email,
          source: 'games',
          source_id: 'games-footer-newsletter',
          site: 'games',
          metadata: trapMeta(),
        }),
      });
      if (!res.ok) throw new Error('failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <footer className="bg-slate-50 dark:bg-[#0F172A] text-slate-900 dark:text-white pt-14 pb-8 border-t border-slate-200 dark:border-white/5" data-testid="games-footer">
      <div className="max-w-6xl mx-auto px-6">
        {/* Top, brand + 4 link columns evenly spaced */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 md:gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link to="/games" className="inline-flex items-center gap-2 mb-4">
              <img src="/games-logo.png" alt="GamesNFans" className="h-11 w-auto" />
            </Link>
            <p className="text-sm text-slate-500 dark:text-gray-400 leading-relaxed max-w-sm">
              GamesNFans is where fans <span className="text-slate-900 dark:text-white font-semibold">read</span> about games &amp; sports, <span className="text-slate-900 dark:text-white font-semibold">shop</span> their setup, <span className="text-slate-900 dark:text-white font-semibold">play</span> indie hits, and soon, <span className="text-purple-600 dark:text-purple-300 font-semibold">create</span> their own with AI.
            </p>
          </div>

          {/* Read */}
          <div>
            <h4 className="text-xs font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest mb-4">{t('readSection', lang)}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/games/cricketer" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('cricketers', lang)}</Link></li>
              <li><Link to="/games/ipl" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('iplHub', lang)}</Link></li>
              <li><Link to="/games/tournament" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('tournaments', lang)}</Link></li>
              <li><Link to="/games/loadout" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('loadoutsShort', lang)}</Link></li>
              <li><Link to="/games/game-guide" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('gameGuides', lang)}</Link></li>
              <li><Link to="/games/guide" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('gearGuides', lang)}</Link></li>
            </ul>
          </div>

          {/* Watch */}
          <div>
            <h4 className="text-xs font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest mb-4">{t('watchPlaySection', lang)}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/games/watch" className="text-sm text-cyan-600 dark:text-cyan-300 hover:text-slate-900 dark:text-white transition-colors">{t('whereToWatchShort', lang)}</Link></li>
              <li><Link to="/games/anime" className="text-sm text-pink-600 dark:text-pink-300 hover:text-slate-900 dark:text-white transition-colors">{t('animeGames', lang)}</Link></li>
              <li><Link to="/games/franchise" className="text-sm text-orange-600 dark:text-orange-300 hover:text-slate-900 dark:text-white transition-colors">{t('moviesGames', lang)}</Link></li>
              <li><Link to="/games/fantasy" className="text-sm text-emerald-600 dark:text-emerald-300 hover:text-slate-900 dark:text-white transition-colors">{t('fantasyHub', lang)}</Link></li>
              <li><Link to="/games/persona" className="text-sm text-rose-600 dark:text-rose-300 hover:text-slate-900 dark:text-white transition-colors">{t('playerPersona', lang)}</Link></li>
              <li><Link to="/games/play" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('arcade', lang)}</Link></li>
            </ul>
          </div>

          {/* Shop + Company */}
          <div>
            <h4 className="text-xs font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest mb-4">{t('shopCreateSection', lang)}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/games/shop" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('gearHub', lang)}</Link></li>
              <li><Link to="/games/builder" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('pcBuilder', lang)}</Link></li>
              <li><Link to="/games/create" className="text-sm text-purple-600 dark:text-purple-300 hover:text-slate-900 dark:text-white transition-colors">{t('aiGameBuilder', lang)}</Link></li>
              <li><Link to="/games/publish" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('publishGame', lang)}</Link></li>
              <li><Link to="/about" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('about', lang)}</Link></li>
              <li><Link to="/contact" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors">{t('contact', lang)}</Link></li>
              <li><Link to="/sitemap" className="text-sm text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:text-white transition-colors" data-testid="games-footer-sitemap">{t('sitemap', lang)}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-white/5 pt-8 pb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="games-newsletter">
          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">{t('newsletterTitle', lang)}</h4>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-1 max-w-sm">{t('newsletterDesc', lang)}</p>
          </div>
          <form onSubmit={handleSubscribe} className="flex flex-wrap items-center gap-2 w-full sm:w-auto relative">
            {trapField}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletterPlaceholder', lang)}
              disabled={status === 'success'}
              className="flex-1 min-w-0 sm:w-56 bg-white dark:bg-white/5 border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-amber-500/60 disabled:opacity-60"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={status === 'loading' || status === 'success'}
              className="shrink-0 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
              data-testid="newsletter-submit-btn"
            >
              {status === 'loading' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {status === 'success' ? t('newsletterSuccess', lang) : t('newsletterButton', lang)}
            </button>
          </form>
        </div>
        {status === 'error' && (
          <p className="text-xs text-red-500 -mt-4 mb-6" data-testid="newsletter-error">{t('newsletterError', lang)}</p>
        )}

        <div className="border-t border-slate-200 dark:border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 dark:text-gray-500">&copy; 2026 GamesNFans.tv</p>
          <p className="text-[11px] text-slate-500 dark:text-gray-500">{t('affiliateDisclosure', lang)}</p>
        </div>
      </div>
    </footer>
    {/* Cross-site network footer, links to all sister sites (CarsBikesPrices, MetroMandi, YourMoneyWise, WokeTraveler, Tech-N-AI Talks).
        Always dark regardless of the page's own theme (its text colors are hardcoded white/light — matches every other site's footer). */}
    <NetworkFooter activeSite="games" containerClass="max-w-6xl mx-auto px-6" />
    </>
  );
}
